import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const QuotesheetEditorModal = ({ isOpen, quotesheet, onClose, onUse, onSave }) => {
  const [editedQuotesheet, setEditedQuotesheet] = useState(quotesheet);
  const [expandedSections, setExpandedSections] = useState(new Set(['vendor-info', 'pricing', 'category-specific']));
  const [addingQuestionToSection, setAddingQuestionToSection] = useState(null);
  const [newQuestion, setNewQuestion] = useState({ label: '', type: 'text' });

  const questionTypeOptions = [
    { value: 'text', label: 'Short Text' },
    { value: 'textarea', label: 'Long Text / Paragraph' },
    { value: 'number', label: 'Number' },
    { value: 'email', label: 'Email' },
    { value: 'tel', label: 'Phone' },
    { value: 'url', label: 'URL / Website' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Dropdown Select' },
  ];

  useEffect(() => {
    if (isOpen && quotesheet) {
      setEditedQuotesheet(quotesheet);
      setExpandedSections(new Set(['vendor-info', 'pricing', 'category-specific', 'project-needs']));
      setAddingQuestionToSection(null);
      setNewQuestion({ label: '', type: 'text' });
    }
  }, [isOpen, quotesheet]);

  if (!isOpen || !quotesheet) return null;

  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded?.has(sectionId)) {
      newExpanded?.delete(sectionId);
    } else {
      newExpanded?.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const toggleFieldRequired = (sectionId, fieldId) => {
    const updatedSections = editedQuotesheet?.sections?.map(section => {
      if (section?.id === sectionId) {
        return {
          ...section,
          fields: section?.fields?.map(field =>
            field?.id === fieldId ? { ...field, required: !field?.required } : field
          )
        };
      }
      return section;
    });
    setEditedQuotesheet({ ...editedQuotesheet, sections: updatedSections });
  };

  const removeField = (sectionId, fieldId) => {
    const updatedSections = editedQuotesheet?.sections?.map(section => {
      if (section?.id === sectionId) {
        return {
          ...section,
          fields: section?.fields?.filter(field => field?.id !== fieldId)
        };
      }
      return section;
    });
    setEditedQuotesheet({ ...editedQuotesheet, sections: updatedSections });
  };

  const updateFieldLabel = (sectionId, fieldId, newLabel) => {
    const updatedSections = editedQuotesheet?.sections?.map(section => {
      if (section?.id === sectionId) {
        return {
          ...section,
          fields: section?.fields?.map(field =>
            field?.id === fieldId ? { ...field, label: newLabel } : field
          )
        };
      }
      return section;
    });
    setEditedQuotesheet({ ...editedQuotesheet, sections: updatedSections });
  };

  const handleStartAddQuestion = (sectionId) => {
    setAddingQuestionToSection(sectionId);
    setNewQuestion({ label: '', type: 'text' });
    const newExpanded = new Set(expandedSections);
    newExpanded?.add(sectionId);
    setExpandedSections(newExpanded);
  };

  const handleCancelAddQuestion = () => {
    setAddingQuestionToSection(null);
    setNewQuestion({ label: '', type: 'text' });
  };

  const handleConfirmAddQuestion = (sectionId) => {
    if (!newQuestion?.label?.trim()) return;
    const customField = {
      id: `custom_${Date.now()}`,
      label: newQuestion?.label?.trim(),
      type: newQuestion?.type,
      required: false,
      isCustom: true,
    };
    const updatedSections = editedQuotesheet?.sections?.map(section => {
      if (section?.id === sectionId) {
        return { ...section, fields: [...(section?.fields || []), customField] };
      }
      return section;
    });
    setEditedQuotesheet({ ...editedQuotesheet, sections: updatedSections });
    setAddingQuestionToSection(null);
    setNewQuestion({ label: '', type: 'text' });
  };

  const handleUseQuotesheet = () => {
    onUse(editedQuotesheet);
  };

  const handleSaveChanges = () => {
    onSave(editedQuotesheet);
  };

  const getTotalFields = () => {
    return editedQuotesheet?.sections?.reduce((total, section) => total + section?.fields?.length, 0);
  };

  const getRequiredFields = () => {
    return editedQuotesheet?.sections?.reduce((total, section) =>
      total + section?.fields?.filter(f => f?.required)?.length, 0
    );
  };

  return (
    <div className="fixed inset-0 z-200 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-60 transition-opacity" onClick={onClose} />
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-primary/20">

          {/* Header — Stackd green gradient */}
          <div className="flex items-center justify-between px-7 py-5 bg-gradient-to-r from-primary to-secondary">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center">
                <Icon name="FileEdit" size={24} className="mr-3 text-white/90" />
                Edit Quotesheet Template
              </h2>
              <p className="text-white/75 text-sm mt-0.5">
                Customize fields and requirements before sharing with vendors
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/20 transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Stats Bar */}
          <div className="bg-primary/5 border-b border-primary/15 px-7 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center gap-1.5">
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="Layers" size={14} className="text-primary" />
                  </div>
                  <span className="text-text-secondary">Sections:</span>
                  <span className="font-bold text-primary">{editedQuotesheet?.sections?.length}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="List" size={14} className="text-primary" />
                  </div>
                  <span className="text-text-secondary">Total Fields:</span>
                  <span className="font-bold text-primary">{getTotalFields()}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-7 h-7 rounded-full bg-warning/10 flex items-center justify-center">
                    <Icon name="AlertCircle" size={14} className="text-warning" />
                  </div>
                  <span className="text-text-secondary">Required:</span>
                  <span className="font-bold text-warning">{getRequiredFields()}</span>
                </div>
              </div>
              <div className="text-xs bg-white border border-primary/20 rounded-full px-3 py-1 text-text-secondary">
                Category: <span className="font-semibold text-primary">{editedQuotesheet?.projectCategory}</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-220px)] p-6 bg-gray-50/50">
            <div className="space-y-4">
              {editedQuotesheet?.sections?.map((section, sectionIndex) => (
                <div
                  key={section?.id}
                  className="border border-primary/20 rounded-xl overflow-hidden shadow-sm bg-white"
                >
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(section?.id)}
                    className="w-full flex items-center justify-between px-5 py-4 bg-gradient-to-r from-primary/8 to-primary/3 hover:from-primary/12 hover:to-primary/6 transition-colors border-b border-primary/10"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-white text-xs font-bold shadow-sm">
                        {sectionIndex + 1}
                      </div>
                      <h3 className="font-semibold text-foreground text-base">{section?.title}</h3>
                      <span className="text-xs bg-primary/10 text-primary font-medium px-2 py-0.5 rounded-full">
                        {section?.fields?.length} field{section?.fields?.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon
                        name={expandedSections?.has(section?.id) ? "ChevronUp" : "ChevronDown"}
                        size={18}
                        className="text-primary"
                      />
                    </div>
                  </button>

                  {/* Section Fields */}
                  {expandedSections?.has(section?.id) && (
                    <div className="p-4 space-y-2.5 bg-white">
                      {section?.fields?.map((field, fieldIndex) => (
                        <div
                          key={field?.id}
                          className="flex items-start gap-3 p-3.5 border border-gray-200 rounded-lg hover:border-primary/40 hover:bg-primary/2 transition-all group bg-white"
                        >
                          {/* Field number badge */}
                          <div className="w-6 h-6 rounded-full bg-gray-100 group-hover:bg-primary/10 flex items-center justify-center text-xs font-semibold text-text-secondary group-hover:text-primary transition-colors flex-shrink-0 mt-1">
                            {fieldIndex + 1}
                          </div>

                          <div className="flex-1 min-w-0">
                            <Input
                              value={field?.label}
                              onChange={(e) => updateFieldLabel(section?.id, field?.id, e?.target?.value)}
                              className="font-medium w-full mb-2"
                            />
                            <div className="flex items-center gap-2">
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-text-secondary border border-gray-200 font-medium">
                                {field?.type}
                              </span>
                              {field?.isCustom && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-medium">
                                  Custom
                                </span>
                              )}
                              {field?.required && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-warning/10 text-warning border border-warning/20 font-medium">
                                  Required
                                </span>
                              )}
                            </div>
                            {field?.description && (
                              <p className="text-xs text-text-secondary mt-1.5 break-words leading-relaxed">{field?.description}</p>
                            )}
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0">
                            <label className="flex items-center gap-1.5 cursor-pointer select-none">
                              <Checkbox
                                checked={field?.required}
                                onChange={() => toggleFieldRequired(section?.id, field?.id)}
                              />
                              <span className="text-xs text-text-secondary whitespace-nowrap">Required</span>
                            </label>
                            <button
                              onClick={() => removeField(section?.id, field?.id)}
                              className="p-1.5 rounded-lg text-error/60 hover:text-error hover:bg-error/10 transition-colors"
                              title="Remove field"
                            >
                              <Icon name="Trash2" size={15} />
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Add Question Form */}
                      {addingQuestionToSection === section?.id ? (
                        <div className="p-4 border-2 border-dashed border-primary/40 rounded-xl bg-primary/5 space-y-3 mt-1">
                          <p className="text-sm font-semibold text-primary flex items-center gap-2">
                            <Icon name="PlusCircle" size={16} className="text-primary" />
                            Add a Custom Question
                          </p>
                          <Input
                            label="Question Text"
                            placeholder="e.g., Do you offer a satisfaction guarantee?"
                            value={newQuestion?.label}
                            onChange={(e) => setNewQuestion(prev => ({ ...prev, label: e?.target?.value }))}
                            autoFocus
                          />
                          <Select
                            label="Answer Type"
                            options={questionTypeOptions}
                            value={newQuestion?.type}
                            onChange={(value) => setNewQuestion(prev => ({ ...prev, type: value }))}
                          />
                          <div className="flex items-center gap-2 pt-1">
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleConfirmAddQuestion(section?.id)}
                              disabled={!newQuestion?.label?.trim()}
                              iconName="Check"
                              iconPosition="left"
                            >
                              Add Question
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleCancelAddQuestion}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleStartAddQuestion(section?.id)}
                          className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-primary/25 rounded-xl text-sm text-text-secondary hover:border-primary/60 hover:text-primary hover:bg-primary/5 transition-all mt-1"
                        >
                          <Icon name="Plus" size={16} />
                          Add a Question to this Section
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Custom Instructions */}
            {editedQuotesheet?.customInstructions && (
              <div className="mt-5 p-4 bg-primary/5 border border-primary/20 rounded-xl">
                <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md bg-primary/15 flex items-center justify-center">
                    <Icon name="MessageSquare" size={13} className="text-primary" />
                  </div>
                  Custom Instructions for Vendors
                </h4>
                <p className="text-sm text-text-secondary leading-relaxed">{editedQuotesheet?.customInstructions}</p>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between px-7 py-4 border-t border-primary/15 bg-primary/3">
            <div className="text-sm text-text-secondary flex items-center gap-1.5">
              <Icon name="Info" size={14} className="text-primary" />
              Changes are saved automatically
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={handleSaveChanges}
                iconName="Save"
                iconPosition="left"
              >
                Save Changes
              </Button>
              <Button
                variant="default"
                onClick={handleUseQuotesheet}
                iconName="CheckCircle"
                iconPosition="left"
              >
                Use This Quotesheet
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotesheetEditorModal;